import React, { useState, useMemo } from 'react';
import { Message, Icon, Table } from 'semantic-ui-react';
import styled from 'styled-components';
import { useApiPortalV1GlossaryDataRetrieveQuery } from '../../../store/generated/portal/glossary';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { PageContent, Title } from '@/common/styles/page';

interface PillarFactor {
  id: string;
  title: string;
  description?: string;
}

interface OutcomeGroup {
  id: string;
  name: string;
  description?: string;
}

interface GlossaryData {
  pillars: PillarFactor[];
  outcomes: OutcomeGroup[];
}


const GlossaryPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<'all' | 'pillars' | 'outcomes'>('all');

  const { data, isLoading, error } = useApiPortalV1GlossaryDataRetrieveQuery();

  const glossaryData = data as GlossaryData | undefined;

  const filteredData = useMemo(() => ({ pillars: activeSection !== 'outcomes' ? glossaryData?.pillars : undefined, outcomes: activeSection !== 'pillars' ? glossaryData?.outcomes : undefined }), [glossaryData, activeSection]);

  if (isLoading) {
    return (
      <PageContent>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem 0' }}>
          <LoadingSpinner />
        </div>
      </PageContent>
    );
  }

  if (error) {
    return (
      <PageContent>
        <Message negative>
          <Message.Header>Error loading glossary</Message.Header>
          <p>Unable to load glossary data. Please try again later.</p>
        </Message>
      </PageContent>
    );
  }

  if (!glossaryData?.pillars?.length && !glossaryData?.outcomes?.length) {
    return (
      <PageContent>
        <Message info>
          <Message.Header>No Data Available</Message.Header>
          <p>There is no glossary data available at this time.</p>
        </Message>
      </PageContent>
    );
  }

  const totalResults = (filteredData.pillars?.length ?? 0) + (filteredData.outcomes?.length ?? 0);

  return (
    <PageContent>
      <ContentSection>
        {(filteredData.pillars?.length ?? 0) > 0 && (activeSection === 'pillars' || activeSection === 'all') && (
          <Section>
            <SectionHeader>
              <Title>Culture Glossary</Title>
            </SectionHeader>
            <SectionDescription>
              These pillars represent the core dimensions of organizational culture.
            </SectionDescription>
{/*             <FilterControl
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              pillarsLength={glossaryData?.pillars?.length ?? 0}
              outcomesLength={glossaryData?.outcomes?.length ?? 0}
            />
 */}
            <StyledTable celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={4}>Name</Table.HeaderCell>
                  <Table.HeaderCell width={12}>Description</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredData.pillars?.map((factor) => (
                  <Table.Row key={factor.id}>
                    <Table.Cell>
                      <strong>{factor.title}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {factor.description || <EmptyText>No description available</EmptyText>}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </StyledTable>
          </Section>
        )}

{/*         {(filteredData.outcomes?.length ?? 0) > 0 && (activeSection === 'outcomes' || activeSection === 'all') && (
          <Section>
            <SectionHeader>
              <SectionIcon name="target" />
              <SectionTitle>Outcomes</SectionTitle>
            </SectionHeader>
            <SectionDescription>
              Outcomes predict business results based on cultural factors, linking workplace
              experience to organizational performance.
            </SectionDescription>
            {activeSection === 'outcomes' &&
            <FilterControl
              activeSection={activeSection}
              setActiveSection={setActiveSection}
              pillarsLength={glossaryData?.pillars?.length ?? 0}
              outcomesLength={glossaryData?.outcomes?.length ?? 0}
            />}
            <StyledTable celled striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell width={4}>Name</Table.HeaderCell>
                  <Table.HeaderCell width={12}>Description</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {filteredData.outcomes?.map((group) => (
                  <Table.Row key={group.id}>
                    <Table.Cell>
                      <strong>{group.name}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      {group.description || <EmptyText>No description available</EmptyText>}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </StyledTable>
          </Section>
        )}
 */}
        {totalResults === 0 && (
          <Message info>
            <Message.Header>No results found</Message.Header>
            <p>Try adjusting your search terms or filters.</p>
          </Message>
        )}
      </ContentSection>
    </PageContent>
  );
};

export default GlossaryPage;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
`;

const FilterTab = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  border: 1px solid ${(props) => (props.$active ? '#3b82f6' : '#d1d5db')};
  background: ${(props) => (props.$active ? '#3b82f6' : 'white')};
  color: ${(props) => (props.$active ? 'white' : '#4b5563')};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${(props) => (props.$active ? '#2563eb' : '#f9fafb')};
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const Section = styled.div`
  background: white;
  border-radius: 8px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const SectionIcon = styled(Icon as any)`
  font-size: 24px !important;
  color: #3b82f6;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: #6b7280;
  margin: 0 0 24px 0;
  line-height: 1.6;
`;

const StyledTable = styled(Table)`
  &&& {
    border: 1px solid #e5e7eb;
    
    thead tr th {
      background: #f9fafb;
      color: #374151;
      font-weight: 600;
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    
    tbody tr {
      &:hover {
        background: #f9fafb;
      }
    }
    
    tbody tr td {
      color: #4b5563;
      font-size: 14px;
      line-height: 1.6;
      
      strong {
        color: #1f2937;
        font-weight: 600;
      }
    }
  }
`;

const EmptyText = styled.span`
  color: #9ca3af;
  font-style: italic;
`;
